import { useEffect, useState } from "react";
import { toast } from "react-toastify";
import {
  useCancelImportJob,
  useImportJobRejections,
  useImportJobStatus,
} from "api/features/import/queries";
import { ApiError } from "api/httpClients/clients/http.client";
import {
  getImportJobTotalRejected,
  isImportJobDisplayFailed,
  isImportJobDisplaySuccess,
} from "./importJobDisplay";
import {
  clearStoredActiveImportJob,
  readStoredActiveImportJob,
  useImportActiveJob,
  writeStoredActiveImportJob,
} from "../../../../../../contexts/contextImportJob";

const IMPORT_TIMEOUT_MS = 15 * 60 * 1000;

export type MutationLike<TPayload, TResult> = {
  mutate: (
    payload: TPayload,
    options?: {
      onSuccess?: (data: TResult) => void;
      onError?: (error: unknown) => void;
      onSettled?: () => void;
    }
  ) => void;
};

type UseUploadImportJobParams = {
  label: string;
  useStartImportJobFunc: () => MutationLike<File, { jobId: string }>;
};

export function useUploadImportJob({ label, useStartImportJobFunc }: UseUploadImportJobParams) {
  const { isAnotherImportRunning, refetchActiveJob, registerLocalJobPolling } =
    useImportActiveJob();
  const [disabled, setDisabled] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [isValidating, setIsValidating] = useState(false);
  const [logExpanded, setLogExpanded] = useState(false);
  const [fileInput, setFileInput] = useState<File | null>(null);
  const [jobId, setJobId] = useState<string | null>(null);
  const [lastNotifiedJobStatus, setLastNotifiedJobStatus] = useState<string | null>(null);
  const [hasRequestedTimeoutCancel, setHasRequestedTimeoutCancel] = useState(false);

  const startImportJob = useStartImportJobFunc();
  const cancelImportJob = useCancelImportJob();
  const importJobQuery = useImportJobStatus(jobId ?? undefined, Boolean(jobId), {
    timeoutMs: IMPORT_TIMEOUT_MS,
  });
  const totalRejected = getImportJobTotalRejected(importJobQuery.data);
  const rejectionsQuery = useImportJobRejections(jobId ?? undefined, {
    enabled: Boolean(jobId) && logExpanded && totalRejected > 0,
    page: 1,
    pageSize: 100,
  });
  const job = importJobQuery.data;
  const isJobDisplayTerminal = Boolean(job?.finishedAt);
  const isImportInProgress = Boolean(jobId) && !isJobDisplayTerminal;
  const isBlockedByOtherImport = isAnotherImportRunning(jobId);

  useEffect(() => {
    if (!jobId || isJobDisplayTerminal) {
      registerLocalJobPolling(jobId, false);
      return;
    }
    registerLocalJobPolling(jobId, true);
    return () => registerLocalJobPolling(jobId, false);
  }, [jobId, isJobDisplayTerminal, registerLocalJobPolling]);

  const persistedRejections = rejectionsQuery.data?.items ?? [];
  const persistedRejectionTotal = rejectionsQuery.data?.total ?? totalRejected;
  const isTimeoutFailure =
    importJobQuery.isTimedOut ||
    (job?.status === "failed" &&
      /(timeout|timed out|cancelled|cancelled by client)/i.test(job?.error ?? ""));

  useEffect(() => {
    setDisabled(!Boolean(fileInput) || isBlockedByOtherImport);
  }, [fileInput, isBlockedByOtherImport]);

  useEffect(() => {
    if (jobId) return;

    const saved = readStoredActiveImportJob();
    if (!saved || saved.label !== label) return;

    setJobId(saved.jobId);
    setLogExpanded(true);
    toast.info(`${label}: resumed tracking import job ${saved.jobId}.`);
  }, [jobId, label]);

  useEffect(() => {
    if (!jobId) return;

    if (isJobDisplayTerminal) {
      const saved = readStoredActiveImportJob();
      if (saved?.jobId === jobId) clearStoredActiveImportJob();
      return;
    }

    writeStoredActiveImportJob(jobId, label);
  }, [isJobDisplayTerminal, jobId, label]);

  useEffect(() => {
    const onBeforeUnload = (event: BeforeUnloadEvent) => {
      if (!isImportInProgress) return;
      event.preventDefault();
      event.returnValue = "";
    };

    window.addEventListener("beforeunload", onBeforeUnload);
    return () => {
      window.removeEventListener("beforeunload", onBeforeUnload);
    };
  }, [isImportInProgress]);

  useEffect(() => {
    const currentJob = importJobQuery.data;
    if (!currentJob?.status) return;

    const rejected = getImportJobTotalRejected(currentJob);
    const displaySuccess = isImportJobDisplaySuccess(currentJob, rejected);
    const displayFailed = isImportJobDisplayFailed(currentJob, rejected);
    const notifyKey = displaySuccess
      ? "success"
      : displayFailed
      ? `failed:${rejected}`
      : currentJob.status;

    if (notifyKey === lastNotifiedJobStatus) return;

    if (displaySuccess) {
      refetchActiveJob();
      toast.success(`${label}: import finished successfully.`);
      setIsValidating(false);
      setLastNotifiedJobStatus(notifyKey);
      return;
    }

    if (displayFailed && (currentJob.status === "failed" || currentJob.finishedAt)) {
      refetchActiveJob();
      const message =
        currentJob.error ||
        (rejected > 0
          ? `Import failed: ${rejected} rejected row(s). Production was not updated.`
          : "Import failed");
      const timeoutAlreadyHandled =
        lastNotifiedJobStatus === "timeout" &&
        /(timeout|timed out|cancelled|cancelled by client)/i.test(message);
      if (timeoutAlreadyHandled) {
        setIsValidating(false);
        setLastNotifiedJobStatus(notifyKey);
        return;
      }
      toast.error(`${label}: ${message}`);
      setIsValidating(false);
      setLastNotifiedJobStatus(notifyKey);
    }
  }, [importJobQuery.data, label, lastNotifiedJobStatus, refetchActiveJob]);

  useEffect(() => {
    if (!jobId) return;

    const error = importJobQuery.error;
    if (!(error instanceof ApiError) || error.status !== 404) return;

    clearStoredActiveImportJob();
    setJobId(null);
    setIsValidating(false);
    setHasRequestedTimeoutCancel(false);
    setLastNotifiedJobStatus(null);

    toast.info(`${label}: import job ${jobId} not found on backend. Tracking has been reset.`);
  }, [importJobQuery.error, jobId, label]);

  useEffect(() => {
    if (!importJobQuery.isTimedOut || !isValidating || !jobId || hasRequestedTimeoutCancel) return;

    setHasRequestedTimeoutCancel(true);

    cancelImportJob.mutate({
      jobId,
      reason: `Import timed out after ${Math.floor(
        IMPORT_TIMEOUT_MS / 60000
      )} minutes and was cancelled.`,
    });

    toast.error(
      `${label}: import timeout after ${Math.floor(
        IMPORT_TIMEOUT_MS / 60000
      )} minutes. The backend job has been cancelled.`
    );
    setIsValidating(false);
    setLastNotifiedJobStatus("timeout");
  }, [
    importJobQuery.isTimedOut,
    isValidating,
    label,
    jobId,
    hasRequestedTimeoutCancel,
    cancelImportJob,
  ]);

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    setFileInput(file ?? null);
  };

  const startImport = (file: File) => {
    if (isBlockedByOtherImport) {
      toast.warning(
        "Another import is already in progress. Wait for it to finish before starting a new one."
      );
      return;
    }

    setIsValidating(true);
    setIsLoading(true);
    setDisabled(true);
    setLogExpanded(true);
    setLastNotifiedJobStatus(null);
    setHasRequestedTimeoutCancel(false);

    startImportJob.mutate(file, {
      onSuccess: (data) => {
        setJobId(data.jobId);
        writeStoredActiveImportJob(data.jobId, label);
        refetchActiveJob();
        toast.success(`${label}: import started (job ${data.jobId}).`);
      },
      onError: (err) => {
        console.error("Error starting import:", err);
        if (err instanceof ApiError && err.status === 409) {
          const conflictJobId =
            typeof err.body === "object" && err.body && "jobId" in err.body
              ? String((err.body as { jobId: string }).jobId)
              : null;
          toast.error(
            conflictJobId
              ? `An import is already running (job ${conflictJobId}). Only one file at a time.`
              : "An import is already running. Only one file at a time."
          );
          refetchActiveJob();
        } else if (err instanceof ApiError && err.status === 413) {
          const sizeMb = (file.size / (1024 * 1024)).toFixed(1);
          toast.error(
            `${label}: file too large (${sizeMb} MB). Maximum upload size is 100 MB. If the file is smaller, the server proxy limit may need to be updated.`
          );
        } else {
          toast.error("Failed to start import. Please try again.");
        }
        setIsValidating(false);
      },
      onSettled: () => {
        setDisabled(false);
        setIsLoading(false);
        setFileInput(null);
      },
    });
  };

  const handleUpdateFile = () => {
    if (!fileInput) return;
    startImport(fileInput);
  };

  return {
    disabled,
    isLoading,
    logExpanded,
    setLogExpanded,
    fileInput,
    handleFileChange,
    handleUpdateFile,
    jobId,
    job,
    totalRejected,
    persistedRejections,
    persistedRejectionTotal,
    rejectionsLoading: rejectionsQuery.isLoading,
    isTimeoutFailure,
    isBlockedByOtherImport,
  };
}
